import { IonLabel } from '@ionic/react';
import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Controller } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';

import './PurpleOptions.scss';

type DisplayType = 'list' | 'grid' | 'grid-with-icon';

interface PurpleOptionsProps {
  TEST_ID?: string;
  className?: string;
  children: NonNullable<React.ReactNode>;
  displayType?: DisplayType;
}

export const PurpleOptions: React.FC<PurpleOptionsProps> = ({
  TEST_ID,
  className,
  children,
  displayType = 'list',
}) => {
  return (
    <div
      className={classNames('purple-options', className, {
        'display-list': displayType === 'list',
        'display-grid': displayType === 'grid',
        'display-grid-with-icon': displayType === 'grid-with-icon',
      })}
      data-testid={TEST_ID}
    >
      {children}
    </div>
  );
};

export const PurpleOptionItem: React.FC<{
  TEST_ID?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  option: {
    id: string;
    title: string;
    icon?: React.ReactNode | string;
  };
  checked: boolean;
  name: string;
  displayType?: DisplayType;
  onChange?: (value: string) => void;
  animate?: boolean;
  show?: boolean;
  expand?: boolean;
}> = ({
  TEST_ID,
  control,
  option,
  checked,
  name,
  displayType = 'list',
  onChange,
  animate,
  show = true,
  expand,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    const scale = checked ? 1.03 : 0.97;

    return (
      <AnimatePresence>
        {show && (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: '100%' },
              visible: { opacity: 1, y: 0, scale },
            }}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <UiTypography
              className={classNames('purple-option-item', {
                checked,
                expand,
                'display-list': displayType === 'list',
                'display-grid': displayType === 'grid',
                'display-grid-with-icon': displayType === 'grid-with-icon',
              })}
              key={option.id}
              TEST_ID={TEST_ID}
              component="label"
              color="black"
              variant="body-medium"
            >
              <Controller
                control={control}
                name={name}
                render={({ field }) => (
                  <input
                    onChange={() => {
                      field.onChange(option.id);
                      onChange && onChange(option.id);
                    }}
                    checked={field.value === option.id}
                    type="checkbox"
                    value={option.id}
                  />
                )}
              />
              {displayType === 'grid-with-icon' && option.icon && (
                <div className="icon-container">
                  {typeof option.icon === 'string' ? option.icon : option.icon}
                </div>
              )}
              <IonLabel>{option.title}</IonLabel>
            </UiTypography>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  const scale = checked ? 1.03 : 1;

  return (
    <UiTypography
      className={classNames('purple-option-item', {
        checked,
        expand,
        'display-list': displayType === 'list',
        'display-grid': displayType === 'grid',
        'display-grid-with-icon': displayType === 'grid-with-icon',
      })}
      key={option.id}
      TEST_ID={TEST_ID}
      component="label"
      color="black"
      variant="body-medium"
      style={{
        scale,
      }}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <input
            onChange={() => {
              field.onChange(option.id);
              onChange && onChange(option.id);
            }}
            checked={field.value === option.id}
            type="checkbox"
            value={option.id}
          />
        )}
      />
      {displayType === 'grid-with-icon' && option.icon && (
        <div className="icon-container">
          {typeof option.icon === 'string' ? option.icon : option.icon}
        </div>
      )}
      <IonLabel>{option.title}</IonLabel>
    </UiTypography>
  );
};
